import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../utils/api';

// GIS publishing: simulation rasters served from the hosted GeoServer as OGC
// WMS/WMTS/WCS. Admin → GIS Services issues per-tenant GeoServer credentials;
// the fire popover's "Add to GIS" publishes one fire's four rasters.

interface DataEnvelope<T> {
  data: T;
}

export interface GisServicesSetting {
  /** Whether a GeoServer is deployed for this environment at all. Publishing
   *  is available to every organisation wherever one is. */
  environment_configured: boolean;
  /** Whether this environment accepts a key in the URL (?authkey=...). */
  authkey_available: boolean;
  /** Days after a fire's start time before it is removed automatically; null = keep. */
  gis_retention_days: number | null;
  retention_choices: (number | null)[];
  workspace: string;
  wms_url: string;
  wmts_url: string;
  wcs_url: string;
  /** The four layers a GIS sees, e.g. "bega_lalc:arrival". */
  layers: string[];
}

export interface GisCredential {
  id: number;
  name: string;
  username: string;
  password_hint: string;
  /** Last 4 of the URL key; empty while the credential is deactivated. */
  authkey_hint: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Creation, password reset, key reset and re-activation each return the
 *  secret they just issued, once. Either field may be absent. */
export interface GisCredentialSecrets extends GisCredential {
  password?: string | null;
  authkey?: string | null;
}

export type PublishedLayerStatus =
  | 'PENDING'
  | 'PUBLISHING'
  | 'PUBLISHED'
  | 'FAILED'
  | 'UNPUBLISHING';

export interface PublishedLayer {
  id: number;
  project: number;
  project_name: string;
  simulation_id: string;
  simulation_name: string;
  partition: string;
  /** The fire's simulated start time — its position on the GIS time axis. */
  fire_time: string | null;
  kind: string;
  status: PublishedLayerStatus;
  workspace: string;
  layer_group: string;
  layers: Record<string, string>;
  publish_stage: string | null;
  publish_error: string | null;
  published_by_username: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SimulationGisPublished {
  /** Tenant entitled and environment configured. */
  enabled: boolean;
  published: PublishedLayer[];
  workspace: string;
  wms_url: string;
  wmts_url: string;
  wcs_url: string;
}

export const isLayerBusy = (row?: PublishedLayer | null) =>
  !!row && (row.status === 'PENDING' || row.status === 'PUBLISHING' || row.status === 'UNPUBLISHING');

// ── tenant setting ───────────────────────────────────────────────────────────

const SETTING_KEY = ['tenant-gis-services'];
const SETTING_URL = '/api/admin/tenant/gis-services';

export const useGisServicesSetting = () =>
  useQuery({
    queryKey: SETTING_KEY,
    queryFn: async () => (await api.request<DataEnvelope<GisServicesSetting>>(SETTING_URL)).data,
    refetchOnWindowFocus: false,
  });

export const useUpdateGisServicesSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: { gis_retention_days: number | null }) =>
      api.request<DataEnvelope<GisServicesSetting>>(SETTING_URL, { method: 'PATCH', body: update }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SETTING_KEY });
    },
  });
};

// ── credentials ──────────────────────────────────────────────────────────────

const CREDS_KEY = ['gis-credentials'];
const CREDS_URL = '/api/admin/gis-credentials';

export const useGisCredentials = () =>
  useQuery({
    queryKey: CREDS_KEY,
    queryFn: () => api.request<GisCredential[]>(CREDS_URL),
  });

const useCredentialMutation = <TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CREDS_KEY });
    },
  });
};

export const useCreateGisCredential = () =>
  useCredentialMutation((data: { name: string }) =>
    api.request<GisCredentialSecrets>(`${CREDS_URL}/`, { method: 'POST', body: data })
  );

export const useResetGisCredentialPassword = () =>
  useCredentialMutation((id: number) =>
    api.request<GisCredentialSecrets>(`${CREDS_URL}/${id}/reset-password`, { method: 'POST' })
  );

export const useResetGisCredentialKey = () =>
  useCredentialMutation((id: number) =>
    api.request<GisCredentialSecrets>(`${CREDS_URL}/${id}/reset-key`, { method: 'POST' })
  );

/** Re-activating issues a fresh key, so this returns secrets too. */
export const useSetGisCredentialActive = () =>
  useCredentialMutation(({ id, active }: { id: number; active: boolean }) =>
    api.request<GisCredentialSecrets>(`${CREDS_URL}/${id}/${active ? 'activate' : 'deactivate'}`, {
      method: 'PATCH',
    })
  );

export const useDeleteGisCredential = () =>
  useCredentialMutation((id: number) => api.request<void>(`${CREDS_URL}/${id}`, { method: 'DELETE' }));

// ── published layers (admin overview) ────────────────────────────────────────

export const usePublishedLayers = () =>
  useQuery({
    queryKey: ['published-layers'],
    queryFn: async () =>
      (await api.request<DataEnvelope<PublishedLayer[]>>('/api/admin/published-layers')).data,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((row) => isLayerBusy(row)) ? 5000 : false,
  });

export const useRemovePublishedLayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.request<DataEnvelope<PublishedLayer> | void>(`/api/admin/published-layers/${id}`, { method: 'DELETE' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['published-layers'] });
      queryClient.invalidateQueries({ queryKey: ['simulation-gis-published'] });
    },
  });
};

// ── per-simulation publish (fire popover) ────────────────────────────────────

const simKey = (projectId: string | undefined, simulationId: string | null | undefined) => [
  'simulation-gis-published',
  projectId,
  simulationId,
];

export const useSimulationGisPublished = (
  projectId: string | undefined,
  simulationId: string | null | undefined
) =>
  useQuery({
    queryKey: simKey(projectId, simulationId),
    queryFn: async () =>
      (
        await api.request<DataEnvelope<SimulationGisPublished>>(
          `/api/projects/${projectId}/simulations/${simulationId}/gis-published`
        )
      ).data,
    enabled: !!projectId && !!simulationId,
    // Poll while a publish/unpublish is in flight so the button follows it.
    refetchInterval: (query) =>
      (query.state.data?.published ?? []).some((row) => isLayerBusy(row)) ? 4000 : false,
    refetchOnWindowFocus: false,
  });

export const usePublishFireToGis = (projectId: string | undefined, simulationId: string | null | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (partition: string) =>
      api.request<DataEnvelope<PublishedLayer>>(
        `/api/projects/${projectId}/simulations/${simulationId}/gis-published`,
        { method: 'POST', body: { partition } }
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: simKey(projectId, simulationId) });
      queryClient.invalidateQueries({ queryKey: ['published-layers'] });
    },
  });
};

export const useUnpublishFireFromGis = (projectId: string | undefined, simulationId: string | null | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (layerId: number) =>
      api.request<DataEnvelope<PublishedLayer> | void>(
        `/api/projects/${projectId}/simulations/${simulationId}/gis-published/${layerId}`,
        { method: 'DELETE' }
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: simKey(projectId, simulationId) });
      queryClient.invalidateQueries({ queryKey: ['published-layers'] });
    },
  });
};
